import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Mail, Send, Eye, ChefHat, Globe, Users, CheckCircle, 
  AlertCircle, Loader2, Play, FileText, BarChart3, Plus,
  Upload, Search, Edit2, Trash2, Save, X, RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import api from '@/lib/api';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const RestaurantEmailCampaign = () => {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLanguage, setPreviewLanguage] = useState('tr');
  const [testEmail, setTestEmail] = useState('');
  const [emailLogs, setEmailLogs] = useState([]);
  
  // Restaurant list management
  const [restaurants, setRestaurants] = useState([]);
  const [restaurantSearch, setRestaurantSearch] = useState('');
  const [loadingRestaurants, setLoadingRestaurants] = useState(false);
  const [editingRestaurant, setEditingRestaurant] = useState(null);
  const [newEmailValue, setNewEmailValue] = useState('');
  
  // Add new email dialog
  const [showAddEmailDialog, setShowAddEmailDialog] = useState(false);
  const [newRestaurantEmail, setNewRestaurantEmail] = useState({
    restaurantId: '',
    email: ''
  });
  
  // Bulk email import
  const [showBulkImportDialog, setShowBulkImportDialog] = useState(false);
  const [bulkEmails, setBulkEmails] = useState('');
  const [importing, setImporting] = useState(false);

  // Language options with flags
  const languages = [
    { code: 'tr', name: 'Türkçe', flag: '🇹🇷', videoAvailable: true },
    { code: 'en', name: 'English', flag: '🇬🇧', videoAvailable: true },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪', videoAvailable: true },
    { code: 'fr', name: 'Français', flag: '🇫🇷', videoAvailable: true },
    { code: 'ar', name: 'العربية', flag: '🇸🇦', videoAvailable: false },
    { code: 'ru', name: 'Русский', flag: '🇷🇺', videoAvailable: false },
    { code: 'fa', name: 'فارسی', flag: '🇮🇷', videoAvailable: false },
  ];

  useEffect(() => {
    fetchStats();
    fetchLogs();
  }, []);

  useEffect(() => {
    fetchPreview(previewLanguage);
  }, [previewLanguage]);

  const fetchStats = async () => {
    try {
      const res = await api.get('/admin/restaurant-emails/stats');
      setStats(res.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
      toast.error('İstatistikler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const fetchLogs = async () => {
    try {
      const res = await api.get('/admin/restaurant-emails/logs');
      setEmailLogs(res.data.logs || []);
    } catch (error) {
      console.error('Failed to fetch logs:', error);
    }
  };

  const fetchPreview = async (lang) => {
    try {
      const res = await api.post(`/admin/restaurant-emails/preview?language=${lang}`);
      setPreviewHtml(res.data.html);
    } catch (error) {
      console.error('Failed to fetch preview:', error);
    }
  };

  const fetchRestaurants = async (search = '') => {
    setLoadingRestaurants(true);
    try {
      const res = await api.get(`/restaurants?search=${search}&limit=50&status=pending_claim`);
      setRestaurants(res.data.restaurants || []);
    } catch (error) {
      console.error('Failed to fetch restaurants:', error);
      toast.error('Restoranlar yüklenemedi');
    } finally {
      setLoadingRestaurants(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (restaurantSearch) {
        fetchRestaurants(restaurantSearch);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [restaurantSearch]);

  const sendTestEmail = async () => {
    if (!testEmail) {
      toast.error('Test e-posta adresi girin');
      return;
    }

    setSendingEmail(true);
    try {
      await api.post('/admin/restaurant-emails/send', {
        test_email: testEmail,
        language: previewLanguage
      });
      toast.success('Test e-postası gönderildi!');
      fetchLogs();
    } catch (error) {
      toast.error('E-posta gönderilemedi');
    } finally {
      setSendingEmail(false);
    }
  };

  const sendBulkEmails = async () => {
    if (!window.confirm(`Tüm ${stats?.pending_claim || 0} restorana e-posta göndermek istediğinize emin misiniz?`)) {
      return;
    }

    setSendingEmail(true);
    try {
      const res = await api.post('/admin/restaurant-emails/send', {
        language: null // Auto-detect by country
      });
      toast.success(`${res.data.count} restorana e-posta gönderimi başlatıldı!`);
      fetchLogs();
    } catch (error) {
      toast.error('E-posta gönderilemedi');
    } finally {
      setSendingEmail(false);
    }
  };

  // Update restaurant email
  const updateRestaurantEmail = async (restaurantId, email) => {
    try {
      await api.put(`/admin/restaurants/${restaurantId}/email`, { email });
      toast.success('E-posta adresi güncellendi!');
      setEditingRestaurant(null);
      setNewEmailValue('');
      fetchRestaurants(restaurantSearch);
      fetchStats();
    } catch (error) {
      toast.error('E-posta güncellenemedi');
    }
  };

  // Bulk import emails
  const handleBulkImport = async () => {
    if (!bulkEmails.trim()) {
      toast.error('E-posta listesi boş');
      return;
    }

    setImporting(true);
    try {
      // Parse CSV/text format: restaurant_id,email or restaurant_name,email
      const lines = bulkEmails.trim().split('\n');
      const emails = lines.map(line => {
        const parts = line.split(',').map(p => p.trim());
        return { identifier: parts[0], email: parts[1] };
      }).filter(e => e.identifier && e.email);

      const res = await api.post('/admin/restaurant-emails/bulk-update', { emails });
      toast.success(`${res.data.updated} restoran e-postası güncellendi!`);
      setShowBulkImportDialog(false);
      setBulkEmails('');
      fetchStats();
    } catch (error) {
      toast.error('Toplu güncelleme başarısız: ' + (error.response?.data?.detail || 'Hata'));
    } finally {
      setImporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-red-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Mail className="w-7 h-7 text-red-600" />
            Restoran E-posta Kampanyası
          </h1>
          <p className="text-muted-foreground mt-1">
            Restoran sahiplerine davet e-postası gönderin
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowBulkImportDialog(true)}>
            <Upload className="w-4 h-4 mr-2" />
            Toplu E-posta Ekle
          </Button>
          <Button variant="outline" onClick={() => { fetchStats(); fetchLogs(); }}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Yenile
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-red-100 flex items-center justify-center">
                <ChefHat className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.total_restaurants || 0}</p>
                <p className="text-sm text-muted-foreground">Toplam Restoran</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-amber-100 flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.pending_claim || 0}</p>
                <p className="text-sm text-muted-foreground">Bekleyen Aktivasyon</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center">
                <Mail className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.with_email || 0}</p>
                <p className="text-sm text-muted-foreground">E-posta Mevcut</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                <Globe className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.by_country?.length || 0}</p>
                <p className="text-sm text-muted-foreground">Ülke Sayısı</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <Tabs defaultValue="preview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="preview" className="flex items-center gap-2">
            <Eye className="w-4 h-4" />
            E-posta Önizleme
          </TabsTrigger>
          <TabsTrigger value="videos" className="flex items-center gap-2">
            <Play className="w-4 h-4" />
            Tanıtım Videoları
          </TabsTrigger>
          <TabsTrigger value="manage" className="flex items-center gap-2">
            <Edit2 className="w-4 h-4" />
            E-posta Yönetimi
          </TabsTrigger>
          <TabsTrigger value="send" className="flex items-center gap-2">
            <Send className="w-4 h-4" />
            Gönderim
          </TabsTrigger>
          <TabsTrigger value="logs" className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4" />
            Gönderim Logları
          </TabsTrigger>
        </TabsList>

        {/* Preview Tab */}
        <TabsContent value="preview">
          <Card>
            <CardHeader>
              <CardTitle>E-posta Şablonu Önizleme</CardTitle>
              <CardDescription>Farklı dillerde e-posta şablonunu görüntüleyin</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="mb-4">
                <Label>Dil Seçin</Label>
                <div className="flex gap-2 mt-2 flex-wrap">
                  {languages.map((lang) => (
                    <Button
                      key={lang.code}
                      variant={previewLanguage === lang.code ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setPreviewLanguage(lang.code)}
                      className="flex items-center gap-2"
                    >
                      <span>{lang.flag}</span>
                      {lang.name}
                      {!lang.videoAvailable && (
                        <Badge variant="secondary" className="text-xs">Video Yok</Badge>
                      )}
                    </Button>
                  ))}
                </div>
              </div>

              <div className="border rounded-lg overflow-hidden">
                <div className="bg-gray-100 p-2 border-b flex items-center gap-2">
                  <FileText className="w-4 h-4 text-gray-500" />
                  <span className="text-sm text-gray-600">E-posta Önizleme</span>
                </div>
                <iframe
                  srcDoc={previewHtml}
                  className="w-full h-[600px] bg-white"
                  title="Email Preview"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Videos Tab */}
        <TabsContent value="videos">
          <Card>
            <CardHeader>
              <CardTitle>Tanıtım Videoları</CardTitle>
              <CardDescription>E-postalarda kullanılan tanıtım videoları</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {languages.filter(l => l.videoAvailable).map((lang) => (
                  <Card key={lang.code} className="overflow-hidden">
                    <div className="aspect-video bg-gradient-to-br from-red-600 to-red-800 flex items-center justify-center relative">
                      <video
                        src={`${API_URL}/api/static/videos/restaurant_promo_${lang.code}.mp4`}
                        controls
                        className="w-full h-full object-cover"
                        poster=""
                      />
                    </div>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-2xl">{lang.flag}</span>
                          <span className="font-medium">{lang.name}</span>
                        </div>
                        <Badge variant="secondary" className="bg-green-100 text-green-700">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Hazır
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                ))}

                {languages.filter(l => !l.videoAvailable).map((lang) => (
                  <Card key={lang.code} className="overflow-hidden opacity-60">
                    <div className="aspect-video bg-gray-200 flex items-center justify-center">
                      <div className="text-center">
                        <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                        <p className="text-gray-500">Video Henüz Oluşturulmadı</p>
                      </div>
                    </div>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-2xl">{lang.flag}</span>
                          <span className="font-medium">{lang.name}</span>
                        </div>
                        <Badge variant="secondary" className="bg-amber-100 text-amber-700">
                          <AlertCircle className="w-3 h-3 mr-1" />
                          Bekliyor
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Email Management Tab */}
        <TabsContent value="manage">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Restoran E-posta Yönetimi</span>
              </CardTitle>
              <CardDescription>Restoran e-posta adreslerini görüntüleyin ve düzenleyin</CardDescription>
            </CardHeader>
            <CardContent>
              {/* Search */}
              <div className="flex gap-4 mb-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Restoran adı veya şehir ara..."
                    value={restaurantSearch}
                    onChange={(e) => setRestaurantSearch(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Button onClick={() => fetchRestaurants(restaurantSearch)}>
                  <Search className="w-4 h-4 mr-2" />
                  Ara
                </Button>
              </div>

              {/* Restaurant Table */}
              {loadingRestaurants ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin" />
                </div>
              ) : restaurants.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <ChefHat className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Restoran aramak için yukarıdaki arama kutusunu kullanın</p>
                </div>
              ) : (
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Restoran</TableHead>
                        <TableHead>Şehir / Ülke</TableHead>
                        <TableHead>E-posta</TableHead>
                        <TableHead className="w-[100px]">İşlem</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {restaurants.map((restaurant) => (
                        <TableRow key={restaurant.id}>
                          <TableCell className="font-medium">{restaurant.name}</TableCell>
                          <TableCell>
                            {restaurant.city}, {restaurant.country}
                          </TableCell>
                          <TableCell>
                            {editingRestaurant === restaurant.id ? (
                              <Input
                                type="email"
                                value={newEmailValue}
                                onChange={(e) => setNewEmailValue(e.target.value)}
                                placeholder="email@example.com"
                                className="max-w-xs"
                              />
                            ) : (
                              <span className={restaurant.email ? '' : 'text-muted-foreground italic'}>
                                {restaurant.email || 'E-posta yok'}
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            {editingRestaurant === restaurant.id ? (
                              <div className="flex gap-1">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => updateRestaurantEmail(restaurant.id, newEmailValue)}
                                >
                                  <Save className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => {
                                    setEditingRestaurant(null);
                                    setNewEmailValue('');
                                  }}
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </div>
                            ) : (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  setEditingRestaurant(restaurant.id);
                                  setNewEmailValue(restaurant.email || '');
                                }}
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Send Tab */}
        <TabsContent value="send">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Test Email */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="w-5 h-5" />
                  Test E-postası Gönder
                </CardTitle>
                <CardDescription>
                  Önce kendinize test e-postası gönderin
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>E-posta Adresi</Label>
                  <Input
                    type="email"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    placeholder="test@example.com"
                  />
                </div>
                <div>
                  <Label>Dil</Label>
                  <Select value={previewLanguage} onValueChange={setPreviewLanguage}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {languages.map((lang) => (
                        <SelectItem key={lang.code} value={lang.code}>
                          {lang.flag} {lang.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button 
                  onClick={sendTestEmail} 
                  disabled={sendingEmail || !testEmail}
                  className="w-full"
                >
                  {sendingEmail ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Test E-postası Gönder
                </Button>
              </CardContent>
            </Card>

            {/* Bulk Send */}
            <Card className="border-red-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-red-600">
                  <Users className="w-5 h-5" />
                  Toplu E-posta Gönder
                </CardTitle>
                <CardDescription>
                  Tüm bekleyen restoranlara e-posta gönder
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-amber-800">
                      <p className="font-medium mb-1">Dikkat!</p>
                      <p>Bu işlem <strong>{stats?.pending_claim || 0}</strong> restorana e-posta gönderecek.</p>
                      <p className="mt-1">Her restoran kendi ülkesine göre otomatik dil seçimi yapılacak.</p>
                    </div>
                  </div>
                </div>

                <div className="text-sm text-muted-foreground">
                  <p><strong>Gönderilecek:</strong> {stats?.pending_claim || 0} restoran</p>
                  <p><strong>E-posta mevcut:</strong> {stats?.with_email || 0} restoran</p>
                </div>

                <Button 
                  onClick={sendBulkEmails}
                  disabled={sendingEmail || !stats?.pending_claim}
                  variant="destructive"
                  className="w-full"
                >
                  {sendingEmail ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Tüm Restoranlara Gönder
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Logs Tab */}
        <TabsContent value="logs">
          <Card>
            <CardHeader>
              <CardTitle>Gönderim Geçmişi</CardTitle>
              <CardDescription>Son gönderilen e-postalar</CardDescription>
            </CardHeader>
            <CardContent>
              {emailLogs.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Mail className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Henüz e-posta gönderilmemiş</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {emailLogs.map((log, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-green-500" />
                        <div>
                          <p className="font-medium">{log.restaurant_name}</p>
                          <p className="text-sm text-muted-foreground">{log.email}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge>{log.language?.toUpperCase()}</Badge>
                        <p className="text-xs text-muted-foreground mt-1">
                          {new Date(log.sent_at).toLocaleString('tr-TR')}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Country Distribution */}
      <Card>
        <CardHeader>
          <CardTitle>Ülkelere Göre Dağılım</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {stats?.by_country?.slice(0, 20).map((country) => (
              <Badge 
                key={country.country} 
                variant="outline" 
                className="px-3 py-1"
              >
                {country.country}: {country.count}
              </Badge>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Bulk Import Dialog */}
      <Dialog open={showBulkImportDialog} onOpenChange={setShowBulkImportDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Toplu E-posta Adresi Ekle</DialogTitle>
            <DialogDescription>
              Restoran ID veya adı ile e-posta adreslerini CSV formatında girin
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                <strong>Format:</strong> Her satıra bir restoran, virgülle ayırın<br />
                <code className="bg-blue-100 px-1 rounded">restoran_id,email@example.com</code><br />
                veya<br />
                <code className="bg-blue-100 px-1 rounded">Restoran Adı,email@example.com</code>
              </p>
            </div>
            
            <div>
              <Label>E-posta Listesi</Label>
              <Textarea
                value={bulkEmails}
                onChange={(e) => setBulkEmails(e.target.value)}
                placeholder={`rest_001,restaurant1@example.com\nAntep Sofrası Berlin,info@antepsofrasiberlin.de\nrest_003,contact@restaurant3.com`}
                rows={10}
                className="font-mono text-sm"
              />
            </div>
            
            <p className="text-sm text-muted-foreground">
              {bulkEmails.trim().split('\n').filter(l => l.trim()).length} satır girildi
            </p>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkImportDialog(false)}>
              İptal
            </Button>
            <Button onClick={handleBulkImport} disabled={importing || !bulkEmails.trim()}>
              {importing ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Upload className="w-4 h-4 mr-2" />
              )}
              E-postaları Ekle
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RestaurantEmailCampaign;
